import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import NotFound from './NotFound';
import PageLayout from '../components/PageLayout';
import { getNewsById, archiveArticle } from '../api';
import ArticleIcon from '../components/ArticleIcon';
import ArticleBody from '../components/ArticleBody';
import ExternalLinkNotice from '../components/ExternalLinkNotice';
import ArticleSidebar from '../components/ArticleSidebar';
import useActiveHeading from '../components/useActiveHeading';
import IconButton from '../components/ui/IconButton';
import PageHeader from '../components/ui/PageHeader';
import PageBody from '../components/ui/PageBody';
import PageMain from '../components/ui/PageMain';
import { extractHeadings } from '../utils/articleHeadings';
import { buildPublicationMeta } from '../utils/publicationMeta';

function NewsDetail() {
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();

  const [news, setNews] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [archiveError, setArchiveError] = useState('');
  const [archiving, setArchiving] = useState(false);

  useEffect(() => {
    let cancelled = false;

    async function loadNews() {
      try {
        setLoading(true);
        setError('');

        const data = await getNewsById(id);

        if (!cancelled) {
          setNews(data);
        }
      } catch (err) {
        console.error('Failed to load news item:', err);

        if (!cancelled) {
          setError('News item does not exist');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    loadNews();
    return () => {
      cancelled = true;
    };
  }, [id]);

  const newsText = news?.text ?? '';
  const headings = useMemo(
    () => extractHeadings(newsText),
    [newsText],
  );
  const activeHeadingId = useActiveHeading(headings);

  const onArchive = async () => {
    if (!news || archiving) {
      return;
    }
    const confirmed = window.confirm(
      'Are you sure you want to move this news item to the archive?',
    );

    if (!confirmed) {
      return;
    }

    try {
      setArchiving(true);
      setArchiveError('');

      await archiveArticle(news.article_id);

      navigate('/news', { replace: true });
    } catch (err) {
      console.error('Failed to archive news item:', err);
      setArchiveError('Failed to move the news item to the archive');
    } finally {
      setArchiving(false);
    }
  };

  if (loading) {
    return (
      <PageLayout
        extraSidebarItems={[
          {
            to: '/news',
            label: t('common.companyNews'),
            icon: 'fa-solid fa-bullhorn',
          },
        ]}
      > <PageMain> <p>{t('newsPage.loading')}</p> </PageMain> </PageLayout>
    );
  }

  if (error || !news) {
    return (
      <NotFound
        eyebrow={t('notFound.newsEyebrow')}
        title={t('notFound.newsTitle')}
        description={t('notFound.newsDescription')}
      />
    );
  }

  return (
    <PageLayout
      extraSidebarItems={[
        {
          to: '/news',
          label: t('common.companyNews'),
          icon: 'fa-solid fa-bullhorn',
        },
      ]}
    > <PageMain className="article-page">
        <PageHeader
          eyebrow={<Link to="/news">
            <i className="fa-solid fa-arrow-left" aria-hidden="true" />
            {t('newsPage.backToNews')} </Link>
          }
          title={news.title}
          actions={
            news.canEdit && (<div className="page-header__actions">
              <IconButton
                className="article-edit-button"
                icon="fa-solid fa-pen-to-square"
                label={t('newsPage.editButtonLabel')}
                to={`/article/${news.article_id}?edit=1`}
              />

              <IconButton
                className="article-edit-button"
                icon="fa-solid fa-trash"
                label={t('articlePage.deleteArchive')}
                onClick={onArchive}
                disabled={archiving}
              />
            </div>
            )
          }
          description={news.summary}
          meta={buildPublicationMeta(news, t, i18n.language)}
          tags={news.tags}
          landscape="behind"
        />

        {archiveError && (
          <p role="alert">{archiveError}</p>
        )}

        <PageBody className="page-body--split">
          <div className="news-article-body">
            {news.icon && (
              <div className="article-banner">
                <ArticleIcon icon={news.icon} alt="" />
              </div>
            )}

            {news.external_url ? (
              <ExternalLinkNotice
                url={news.external_url}
                canEdit={news.canEdit}
              />
            ) : (
              <ArticleBody
                articleText={newsText}
                headings={headings}
              />
            )}
          </div>

          <ArticleSidebar
            headings={headings}
            activeHeadingId={activeHeadingId}
          />
        </PageBody>
      </PageMain>
    </PageLayout>

  );
}

export default NewsDetail;
